import { DB } from './db';

import {
  exemplarDataClassic,
  exemplarDataAnnot,
  scenarioData,
  skillDescriptionData,
  skillData,
  tagData,
  annotationDataClassic,
  annotationDataAnnot,
  peerResultsData,
  avoidReasonsData,
  evalExemplarsData,
  fixedOptionExemplarsData,
  fixedOptionsData
} from './data';

import {
  Annotation as DBAnnotation,
  Exemplar,
  ITSData,
  PeerResult,
  Scenario as DBScenario,
  Skill,
  SkillDescription,
  Tag as DBTag,
  AvoidReason,
  EvalExemplar,
  TagAvoid as TagAvoidDB,
  FixedOptionExemplar,
  FixedOption,
  ExemplarWithFixed,
} from './its-db-types';
import {
  asyncForEach,
  removeRandomArrayElement,
  shuffleArray,
} from '../utils/utils';
import {
  Range,
  Hints,
  HintsDefinition,
  PatientData,
  ProblemData,
  RawInputPageData,
  RawIntroPageData,
  RawPageData,
  ResponseDefinition,
  Scenario,
  SFFProblemType,
  TagData,
  Tag,
  Tags,
  TagText,
  RawEvalPageData,
  TagsAvoid,
  RawModuleCompletionPageData,
  DiscourseAnnotation,
  RawDiscoursePageData,
  RawFixedOptionPageData,
  RawFixedOptions,
  RawDiscourseFixedPageData,
  FixedOptionInfo,
  DiscourseCustomFeedback,
  DisourceCustomFeedbackResponse
} from '../common/page-data';

import PouchDB from 'pouchdb-browser';
// import MemoryAdapter from 'pouchdb-adapter-memory';
import IndexAdapter from 'pouchdb-find';
import { generateAnnotationOptions } from './discourse-options';
import { MersenneTwister } from '../utils/mersenne-twister';
// import idbAdapter from 'pouchdb-adapter-indexeddb';

PouchDB.plugin(IndexAdapter);
// PouchDB.plugin(idbAdapter);

const exemplarData = [...exemplarDataClassic, ...exemplarDataAnnot];
const annotationData = [...annotationDataClassic, ...annotationDataAnnot];

export interface GetProblemRequest {
  /** The set of example ids to NOT be used when generating a problem */
  ignore: string[];
  /**
   * main doesn't seem to serve a purpose, secondary is a difficulty tier for a problem type.
   * 0 is 'least' difficult tier and increases from there.
   * Only applicable for certain problem types, default should be 0, 0
   **/
  level: { main: number; secondary: number };
  skill: string;
  type: SFFProblemType;

  /**
   * Seed value used to initialize the random number generator used to compose certain elements of a
   * problem. Used for consistency, a two problems with same seed and other params should look exactly the
   * same.
   * Set to negative value to pick a random seed.
   */
  seed: number;

  /** If specified the generated problem should use this specific example */
  exemplarId?: string;

  /** Tag id that should be part of the correct answer */
  requiredCorrectTag?: string;
  /** Tag id that should be distractor (i.e. incorrect answer) */
  requiredIncorrectTag?: string;
}

/**
 * Request payload for fetching custom feedback for exemplars (i.e. provider responses)
 */
type ResponseDefFeedbackRequest = {
  skillId: string;
  scenarioId: string;
  exemplarId: string;
};

type FeedbackTagElement = {
  AttributeId: string;
  Val: string;
  Explanation: string;
};

type Feedback = {
  ResponseId: string;
  Results: FeedbackTagElement[];
}

type ResponseDefFeedbackResult = {
  // Reference to original request
  req: ResponseDefFeedbackRequest;
  // String file contents
  contents: Feedback | null;
  // true if response feedback could be fetched
  success: boolean;
};

type problemGeneratorFn = ((
  request: GetProblemRequest
) => Promise<RawPageData | undefined>) | undefined;
type typeGeneratorMap = {
  [type in SFFProblemType]: problemGeneratorFn;
};

const noneOfTheAboveOption: ResponseDefinition = {
  correct: true,
  id: 'SYSTEM',
  text: {
    declarative: 'None of the above',
    imperative: 'None of the above',
    gerund: 'None of the above',
  },
  reason: [],
  quality: 0,
  tagData: [],
};

const avoidReasonDB = await new DB<AvoidReason>('avoidReasons').setup(
  avoidReasonsData
);
const exemplarDB = await new DB<Exemplar>('exemplars').setup(exemplarData);
const evalExemplarDB = await new DB<EvalExemplar>('evalExemplars').setup(
  evalExemplarsData
);
const scenarioDB = await new DB<DBScenario>('scenarios').setup(scenarioData);
const skillDescriptionDB = await new DB<SkillDescription>(
  'skillDescriptions'
).setup(skillDescriptionData);
const skillDB = await new DB<Skill>('skills').setup(skillData);
const tagDB = await new DB<DBTag>('tags').setup(tagData);
const annotationDB = await new DB<DBAnnotation>('annotations').setup(
  annotationData
);
const peerResultsDB = await new DB<PeerResult>('peerResults').setup(
  peerResultsData
);
const fixedOptionDB = await new DB<FixedOption>('fixedOptions').setup(
  fixedOptionsData
);
const fixedOptionExemplarDB = await new DB<FixedOptionExemplar>('fixedOptionExemplar').setup(
  fixedOptionExemplarsData
);

export class ProblemGenerator {
  private random: MersenneTwister;

  private constructor() {
    this.random = new MersenneTwister();

    tagDB.createIndex({ index: { fields: ['skill'] } });
    tagDB.createIndex({ index: { fields: ['id'] } });
    exemplarDB.createIndex({
      index: { fields: ['scenario', 'type', 'skills'] },
    });
    evalExemplarDB.createIndex({ index: { fields: ['skill', 'id'] } });
    peerResultsDB.createIndex({ index: { fields: ['exemplar'] } });
  }

  private static instance: ProblemGenerator;

  private typeGenerators: typeGeneratorMap = {
    [SFFProblemType.ModuleIntro]: this.generateP0.bind(this),
    [SFFProblemType.Type1]: this.generateP1.bind(this),
    [SFFProblemType.Type2]: this.generateP2.bind(this),
    [SFFProblemType.Type3]: this.generateP3.bind(this),
    [SFFProblemType.Type4]: this.generateP4.bind(this),
    [SFFProblemType.Type6]: this.generateP6.bind(this),
    [SFFProblemType.Discourse]: this.generateDiscourse.bind(this),
    [SFFProblemType.DiscourseFixed]: this.generateDiscourseWithFixed.bind(this),
    [SFFProblemType.SelfEval]: this.generateSelfEval.bind(this),
    [SFFProblemType.PeerEval]: this.generatePeerEval.bind(this),
    [SFFProblemType.ModuleCompletion]: this.generateModuleCompletion.bind(this),
    [SFFProblemType.FixedOption]: this.generateFixedOption.bind(this),
    
    [SFFProblemType.Type5]: undefined,
    [SFFProblemType.Type7]: undefined,
    [SFFProblemType.AnnotateMulti]: undefined,
    [SFFProblemType.AnnotateSingle]: undefined,
    [SFFProblemType.TagAnnotation]: undefined,
    [SFFProblemType.SkillCompletion]: undefined,
  };

  public static getInstance(): ProblemGenerator {
    if (!ProblemGenerator.instance) {
      ProblemGenerator.instance = new ProblemGenerator();
    }

    return ProblemGenerator.instance;
  }

  private resetRandomForRequest({ seed }: GetProblemRequest) {
    if (seed > 0) {
      this.random.initGenrand(seed);
    } else {
      // Use 'random' seed if one not explicitly given
      this.random.initGenrand();
    }
  }

  private getRandomProblemQuality() {
    return this.random.random() >= 0.5;
  }

  private async getSkillFromName(skillName: string): Promise<Skill> {
    const skill: Skill = await skillDB.getDocByName(skillName);
    return skill;
  }
  private async getSkillDescriptionFromId(
    skill: string
  ): Promise<SkillDescription> {
    const skillDescription = await skillDescriptionDB.getDocBySelector({
      skill,
    });

    return skillDescription[0];
  }
  private async getTagsFromSkillId(skill: string): Promise<DBTag[]> {
    const tags = await tagDB.getDocBySelector({ skill });
    return tags.sort((a, b) => (a.id < b.id) ? -1 : 1);
  }

  private async getTagsFromSkillIdAndQuality(
    skill: string,
    quality: number
  ): Promise<DBTag[]> {
    const tags = await tagDB.getDocBySelector({
      skill,
      quality,
    });

    return tags.sort((a, b) => (a.id < b.id) ? -1 : 1);
  }

  private async getScenarioFromId(id: string): Promise<DBScenario> {
    const scenario = await scenarioDB.getDocFromId(id);
    return scenario;
  }

  private async getScenariosFromSkillId(skill: string): Promise<DBScenario[]> {
    const scenarios = await scenarioDB.getDocBySelector({
      skills: { $elemMatch: { $eq: skill } },
    });

    // Selected scenarios are in a random order coming out of selector
    return scenarios.sort((a, b) => (a.id < b.id) ? -1 : 1);
  }

  private async getAnnotationExemplarFromId(id: string): Promise<Exemplar> {
    const exemplar = await exemplarDB.getDocFromId(id);
    return exemplar;
  }

  private async getAnnotationExemplarsForSkill(
    type: string,
    skill: string
  ): Promise<Exemplar[]> {
    const exemplars = await exemplarDB.getDocBySelector({
      skills: { $elemMatch: { $eq: skill } },
      type,
    });

    return exemplars.sort((a, b) => (a.id < b.id) ? -1 : 1);
  }

  public async getEvalSkills() {
    const skills = new Set<string>();
    const allExemplars = await evalExemplarDB.getAllDocs();
    for (const docId in allExemplars) {
      const exemplar = allExemplars[docId];
      skills.add(exemplar.skill);
    }
    return Array.from(skills.values());
  }

  private async getEvalExemplarsForSkill(
    skill: string
  ): Promise<EvalExemplar[]> {
    const evalExemplars = await evalExemplarDB.getDocBySelector({ skill });
    return evalExemplars.sort((a, b) => (a.id < b.id) ? -1 : 1);
  }

  private async getEvalExemplarFromId(id: string): Promise<EvalExemplar> {
    const exemplar = await evalExemplarDB.getDocFromId(id);

    return exemplar;
  }

  private async getPeerResultFromExemplarId(
    id: string
  ): Promise<PeerResult | undefined> {
    const peerResults = await peerResultsDB.getDocBySelector({
      id
    });

    return peerResults[0];
  }

  private async getPeerResultsForSkill(skill: string): Promise<PeerResult[]> {
    const exemplars = await peerResultsDB.getDocBySelector({
      skill,
    });

    return exemplars;
  }

  private async getFixedOptionExemplarFromId(id: string): Promise<FixedOptionExemplar> {
    const exemplar = await fixedOptionExemplarDB.getDocFromId(id);

    return exemplar;
  }

  private async getFixedOptionExemplarsForSkill(
    skillId: string
  ): Promise<FixedOptionExemplar[]> {
    const exemplars = await fixedOptionExemplarDB.getDocBySelector({
      skillId
    });

    return exemplars.sort((a, b) => (a.id < b.id) ? -1 : 1);
  }

  private async getFixedOptionsFromId(
    id: string
  ): Promise<FixedOption> {
    const options = await fixedOptionDB.getDocBySelector({
      id
    });

    return options[0];
  }

  private async getExemplarsFromScenarioIdAndSkill(
    scenario: string,
    skill: string
  ): Promise<Exemplar[]> {
    const exemplars = await exemplarDB.getDocBySelector({
      scenario,
      type: 'default',
      skills: { $elemMatch: { $eq: skill } },
    });

    return exemplars.sort((a, b) => (a.id < b.id) ? -1 : 1);
  }

  private async getAnnotationForExemplar(
    exemplar: string
  ): Promise<DBAnnotation[]> {
    const annotations = await annotationDB.getDocBySelector({
      exemplar,
    });

    return annotations.sort((a, b) => (a.id < b.id) ? -1 : 1);
  }

  private async getTagsFromExemplarId(exemplar: string): Promise<DBTag[]> {
    const annotations = await annotationDB.getDocBySelector({
      exemplar,
    });

    const tagsIds: string[] = annotations[0].tags;
    const tags: DBTag[] = [];

    await asyncForEach(tagsIds, async (tagId): Promise<void> => {
      const tag = await tagDB.getDocFromId(tagId);
      tags.push(tag);
    });

    return tags.sort((a, b) => (a.id < b.id) ? -1 : 1);
  }

  private scenarioFromDBScenario(dbScenario: DBScenario): Scenario {
    const scenario: Scenario = {
      id: dbScenario.id,
      metadata: { ...dbScenario.metadata } as unknown as PatientData,
      content: dbScenario.content,
      name: dbScenario.name,
    };

    return scenario;
  }
  private async calcExemplarQualityAndTags(
    exemplars: Exemplar[]
  ): Promise<void> {
    await asyncForEach(exemplars, async (exemplar): Promise<void> => {
      const tags = await this.getTagsFromExemplarId(exemplar.id);

      exemplar.tags = tags;
      let quality = 0;

      tags.forEach((tag): void => {
        quality += tag.quality;
      });

      quality /= tags.length;

      exemplar.quality = quality;
    });
  }

  private discourseAnnotationsFromDBAnnotations(
    dbAnnotations: DBAnnotation[]
  ): DiscourseAnnotation[] {
    const annotations: DiscourseAnnotation[] = [];

    dbAnnotations.forEach((dbAnnotation: DBAnnotation): void => {
      const { tags, /*tagsAvoid,*/ id, range = {} } = dbAnnotation;
      const { anchor, focus } = range as Range;
      annotations.push({
        id,
        anchor,
        focus,
        tag: tags[0],
      });
    });

    return annotations;
  }

  private tagsFromRawTags(rawTags: DBTag[], tagIdFilter?: string[]): Tags {
    const tags: Tags = {};

    rawTags.forEach((tag: DBTag): void => {
      if (!tagIdFilter || tagIdFilter.indexOf(tag.id) >= 0) {
        const { id, name, reason, explanation, quality, skill } = tag;
        tags[tag.id] = { id, name, reason, explanation, quality, skill };
      }
    });

    return tags;
  }

  private async avoidTagsFromRawTags(
    rawTags: DBTag[],
    tagsAvoidDB: TagAvoidDB[]
  ): Promise<TagsAvoid> {
    const tagsAvoid: TagsAvoid = {};

    const rawTagsObject: Record<string, DBTag> = {};
    rawTags.forEach((tag) => {
      rawTagsObject[tag.id] = tag;
    });

    for (const tagAvoid of tagsAvoidDB) {
      const tag = rawTagsObject[tagAvoid.tag];
      const { id, name, reason, explanation, quality, skill } = tag;

      const avoidReason = (await avoidReasonDB.getDocFromId(tagAvoid.reasonId))
        .reason;

      tagsAvoid[tag.id] = {
        id,
        name,
        reason,
        explanation,
        quality,
        skill,
        avoidReason,
      };
    }

    return tagsAvoid;
  }

  /**
   * From set of options select a subset
   * 
   * @param ignore
   *    The items in tsIn that should not be selected
   * @param tsIn
   *    List of items to pick from 
   * @param maxT
   *    max number of target items that can be picked
   * @param maxD 
   *    max number of distractor items that can be picked
   * @param max
   *    max number of total items to pick 
   * @param min 
   *    min number of total item needed to be picked
   * @param isTarget
   *    callback that returns 1 if an item from tsIn is a target that can be picked, -1 if it a distractor
   *    that can be picked, 0 if it should not be picked
   * @returns
   *    List of items picked from tsIn, undefined is not enough items to meet the given min threshold
   */
  private chooseTargetsAndDistractors<T extends ITSData>(
    ignore: string[],
    tsIn: T[],
    maxT: number,
    maxD: number,
    max: number,
    min: number,
    isTarget: (t: T) => number // 1: yes, -1: no, 0: discard
  ): T[] | undefined {
    const itemsToExamine = [...tsIn];
    const itemsPicked: T[] = [];
    let numTargetsPicked = 0;
    let numDistractorsPicked = 0;

    while (itemsPicked.length < max && itemsToExamine.length > 0) {
      const t = removeRandomArrayElement(itemsToExamine, this.random);

      if (ignore.includes(t.id)) continue;

      const isTargetResult = isTarget(t);

      if (isTargetResult > 0) {
        if (numTargetsPicked >= maxT) continue;
        numTargetsPicked++;
      } else if (isTargetResult < 0) {
        if (numDistractorsPicked >= maxD) continue;
        numDistractorsPicked++;
      } else {
        continue;
      }

      itemsPicked.push(t);
    }

    if (itemsPicked.length >= min) return itemsPicked;

    // Not enough items to pick
    return undefined;
  }

  private async getHintsForSkillAndQuality(
    skill: string,
    quality: boolean
  ): Promise<Hints> {
    const tags: DBTag[] = await this.getTagsFromSkillIdAndQuality(
      skill,
      quality ? 1 : -1
    );

    const hints: HintsDefinition[] = tags.map((tag: DBTag): HintsDefinition => {
      const hint: HintsDefinition = {
        id: tag.id,
        explanation: tag.explanation,
        reason: tag.reason,
        quality: tag.quality,
        name: tag.name,
      };

      return hint;
    });

    return hints;
  }

  private async generateP0(
    request: GetProblemRequest
  ): Promise<RawIntroPageData | undefined> {
    const skill: Skill = await this.getSkillFromName(request.skill);

    const skillDescription = await this.getSkillDescriptionFromId(skill.id);
    const data: ProblemData = {
      markdown: skillDescription.content?.markdown || '',
    };
    const type = request.type;

    const rawIntroPageData: RawIntroPageData = {
      seed: this.random.seed,
      data,
      type,
      skill,
      ignoreList: [],
    };

    return rawIntroPageData;
  }

  private async generateModuleCompletion(
    request: GetProblemRequest
  ): Promise<RawModuleCompletionPageData | undefined> {
    const skill: Skill = await this.getSkillFromName(request.skill);
    const type = request.type;
    const skillDescription = await this.getSkillDescriptionFromId(skill.id);
    const data: ProblemData = {
      markdown: skillDescription.content?.markdown || '',
    };

    const moduleCompletionPageData: RawModuleCompletionPageData = {
      seed: this.random.seed,
      type,
      problemQuality: false,
      skill,
      data,
      ignoreList: [],
    };

    return moduleCompletionPageData;
  }

  /**
   * P1 type questions asks the user to select statements that are a good/poor demonstration of the skill.
   * The statements themselves are directly correlated to tags.
   * 
   * @param request 
   * @returns 
   */
  private async generateP1(
    request: GetProblemRequest
  ): Promise<RawInputPageData | undefined> {
    this.resetRandomForRequest(request);

    let problemQuality = this.getRandomProblemQuality();
    const problemType = 'multiChoice';
    const { type, requiredCorrectTag, ignore } = request;
    const ignoreList: string[] = [];
    const tagsToIgnore = requiredCorrectTag ? [...ignore, requiredCorrectTag] : ignore;
    let matchingRequiredTag: DBTag | undefined;
    const skill: Skill = await this.getSkillFromName(request.skill);

    const allTagsForSkill = await this.getTagsFromSkillId(skill.id);

    const { secondary } = request.level;

    let maxT, maxD, max;

    /*
    Check quality of required tag and include it as an option
    */
   let requiredTagsToAddLater = 0;
   if (requiredCorrectTag) {
    matchingRequiredTag = allTagsForSkill.find((tag) => tag.id === requiredCorrectTag);
    if (matchingRequiredTag) {
      problemQuality = matchingRequiredTag.quality > 0;
      requiredTagsToAddLater = 1;
    }
   }

    // can't use switch, compiler is changing break into return
    if (secondary === 0) {
      maxT = 3 - requiredTagsToAddLater;
      maxD = 0;
      max = 3 - requiredTagsToAddLater;
    } else if (secondary === 1) {
      maxT = 0;
      maxD = 2 - requiredTagsToAddLater;
      max = 2 - requiredTagsToAddLater;
    } else {
      maxT = 2;
      maxD = 2;
      max = 3 - requiredTagsToAddLater;
    }

    const isTarget = (tag: DBTag): number => {
      const tagQuality = tag.quality > 0;
      return !tagQuality === !problemQuality ? 1 : -1;
    };

    let tags = this.chooseTargetsAndDistractors(
      tagsToIgnore,
      allTagsForSkill,
      maxT,
      maxD,
      max,
      max,
      isTarget
    );

    if (tags && matchingRequiredTag) {
      tags.push(matchingRequiredTag);
    }

    // if we didn't find anything, flip the quality
    // don't do this if a required tag is specified since we need to force matching quality in that case
    if (!tags && !requiredCorrectTag) {
      problemQuality = !problemQuality;
      tags = this.chooseTargetsAndDistractors(
        tagsToIgnore,
        allTagsForSkill,
        maxT,
        maxD,
        max,
        max,
        isTarget
      );
    }

    // if we still didn't find anything, bail
    if (!tags) return;

    const options: ResponseDefinition[] = tags.map(
      (tag): ResponseDefinition => {
        const tagQuality = tag.quality > 0;
        const correct = problemQuality === tagQuality;

        if (correct) ignoreList.push(tag.id);

        return {
          correct,
          id: tag.id,
          text: tag.reason,
          name: tag.name,
          reason: [
            {
              declarative: tag.explanation.markdown,
              imperative: tag.explanation.markdown,
              gerund: tag.explanation.markdown,
            },
          ],
          quality: tag.quality,
          // metadata: tag.metadata,
          tagData: [{ id: tag.id, name: tag.name }],
        };
      }
    );

    if (secondary === 1) {
      options.push({ ...noneOfTheAboveOption });
    }

    const hints: HintsDefinition[] = [];

    const rawInputPageData: RawInputPageData = {
      problemQuality,
      problemType,
      type,
      skill,
      options,
      hints,
      seed: this.random.seed,
      ignoreList,
    };
    return rawInputPageData;
  }

  /**
   * P2 style questions have a scenario with a patient statement or context, the question asks the user to
   * pick a statement (an exemplar) that demonstrates or does not demonstrate the skill.
   * That each statement is associated either positively or negatively with the skill, the question asks
   * to match the positive or negative.
   * 
   * @param request 
   * @returns 
   */
  private async generateP2(
    request: GetProblemRequest
  ): Promise<RawInputPageData | undefined> {
    this.resetRandomForRequest(request);

    const { secondary } = request.level;
    let problemQuality = this.getRandomProblemQuality();
    const problemType = secondary ? 'multiChoice' : 'singleChoice';
    const { type, requiredCorrectTag, ignore } = request;
    const exemplarsToIgnoreInGeneration = requiredCorrectTag ? [...ignore, requiredCorrectTag] : ignore;
    let exemplarMatchingTag: Exemplar | undefined;
    const ignoreList: string[] = [];
    const skill: Skill = await this.getSkillFromName(request.skill);
    const scenarios: DBScenario[] = await this.getScenariosFromSkillId(
      skill.id
    );

    let dbScenario: DBScenario = scenarios[0];
    let exemplars: Exemplar[] | undefined;

    const isTarget = (exemplar: Exemplar): number => {
      const exemplarQuality = exemplar.quality > 0;
      return !exemplarQuality === !problemQuality ? 1 : -1;
    };

    while (!exemplars && scenarios.length) {
      dbScenario = removeRandomArrayElement(scenarios, this.random);

      const allExemplarsForScenario: Exemplar[] =
        await this.getExemplarsFromScenarioIdAndSkill(dbScenario.id, skill.id);
      await this.calcExemplarQualityAndTags(allExemplarsForScenario);

      // Randomly pick an exemplar that matches the tag condition.
      // Filter out only exemplars that include the correct tag.
      let targetsToManuallyAdd = 0;
      if (requiredCorrectTag) {
        const exemplarsMatchingTag = allExemplarsForScenario.filter((exemplar) => {
          return exemplar.tags.find((tag) => tag.id === requiredCorrectTag);
        });
        if (exemplarsMatchingTag.length > 0) {
          // Randomly pick one
          // Change the problem quality to match the tag
          exemplarMatchingTag = removeRandomArrayElement(exemplarsMatchingTag, this.random);
          problemQuality = exemplarMatchingTag.tags[0].quality > 0;
          targetsToManuallyAdd = 1;
        } else {
          // Pick different scenario if no exemplar can fit requirement
          continue;
        }
        
        // Force it to be added to exemplar list
      }

      let maxT, maxD, max;

      // can't use switch, compiler is changing break into return
      if (secondary === 0) {
        maxT = 1 - targetsToManuallyAdd;
        maxD = 2;
        max = 3 - targetsToManuallyAdd;
      } else {
        maxT = 2 - targetsToManuallyAdd;
        maxD = 2;
        max = 3 - targetsToManuallyAdd;
      }

      exemplars = this.chooseTargetsAndDistractors(
        exemplarsToIgnoreInGeneration,
        allExemplarsForScenario,
        maxT,
        maxD,
        max,
        max,
        isTarget
      );
      if (exemplars && exemplarMatchingTag) {
        exemplars.push(exemplarMatchingTag)
      }

      // if we didn't find anything, flip the quality
      // also don't flip quality if a required tag is specified, we want to force a specific quality in that
      // case.
      if (!exemplars && !requiredCorrectTag) {
        problemQuality = !problemQuality;
        exemplars = this.chooseTargetsAndDistractors(
          exemplarsToIgnoreInGeneration,
          allExemplarsForScenario,
          maxT,
          maxD,
          max,
          max,
          isTarget
        );
      }
    }

    // No scenarios match the requirements, cannot generate a problem of this type
    if (!exemplars) return;
    
    

    // This question type asks generally whether a selection of provider responses is good/poor
    // Custom feedback per response should simply pick the skill component (just first one if multiple) that
    // applies to a provider response and display that one
    const feedbackResults = await this.getResponseDefinitionFeedbackFromExemplars(exemplars, skill.id, dbScenario.id);

    const options: ResponseDefinition[] = exemplars.map(
      (exemplar): ResponseDefinition => {
        const exemplarQuality = exemplar.quality > 0;
        const correct = problemQuality === exemplarQuality;
        const reason: TagText[] = exemplar.tags.map((tag): TagText => {
          return tag.reason;
        });
        const tagData: TagData[] = exemplar.tags.map((tag): TagData => {
          const { id, name } = tag;
          return { id, name };
        });

        if (correct) ignoreList.push(exemplar.id);

        const customFeedback = (tagData.length > 0) ?
          this.getFeedbackExplanationForExamplarAndTag(feedbackResults, exemplar, tagData[0].id) : undefined;

        return {
          correct,
          id: exemplar.id,
          content: exemplar.content,
          quality: exemplar.quality,
          reason,
          tagData,
          customReviewFeedback: customFeedback
        };
      }
    );

    const scenario = this.scenarioFromDBScenario(dbScenario);

    const hints = await this.getHintsForSkillAndQuality(
      skill.id,
      problemQuality
    );

    const rawInputPageData: RawInputPageData = {
      problemQuality,
      problemType,
      type,
      skill,
      options,
      hints,
      seed: this.random.seed,
      scenario,
      ignoreList,
    };
    return rawInputPageData;
  }

  /**
   * P3 style questions presents a therapist statement for a patient scenario and asks the user
   * to pick out the 'tag' demonstrating the reason the statement (also called exemplar) is good/bad
   * 
   * @param request 
   * @returns 
   */
  private async generateP3(
    request: GetProblemRequest
  ): Promise<RawInputPageData | undefined> {
    this.resetRandomForRequest(request);

    const { secondary } = request.level;
    const { type, ignore, requiredCorrectTag, requiredIncorrectTag } = request;
    const problemType = secondary === 2 ? 'multiChoice' : 'singleChoice';
    const ignoreList: string[] = [];
    const hints: HintsDefinition[] = [];
    const skill: Skill = await this.getSkillFromName(request.skill);
    const scenarios: DBScenario[] = await this.getScenariosFromSkillId(
      skill.id
    );

    const allPossibleRawTags = await this.getTagsFromSkillId(skill.id);

    let tags: DBTag[] | undefined;
    let selectedCorrectTags: DBTag[] = [];
    let originalScenarioTags: DBTag[] = [];

    const isTarget = (tag: DBTag): number => {
      // if this is a current tag, it is a target
      for (let i = 0; i < selectedCorrectTags.length; i++) {
        if (tag.id === selectedCorrectTags[i].id) return 1;
      }
      // it can only be a distractor if it is not a scenario tag
      for (let i = 0; i < originalScenarioTags.length; i++) {
        if (tag.id === originalScenarioTags[i].id) return 0;
      }

      // for secondary level 0, everything else is a distractor
      if (secondary === 0) return -1;

      // otherwise it is only a distractor if the quality is the same
      if (tag.quality === selectedCorrectTags[0].quality) return -1;
      else return 0;
    };

    const currentTagsMax = secondary === 2 ? 2 : 1;
    let currentExemplar: Exemplar | null = null;

    let dbScenario: DBScenario = scenarios[0];
    while (!tags && scenarios.length) {
      dbScenario = removeRandomArrayElement(scenarios, this.random);

      // get all therapist statements for the selected scenario
      const rawExemplars: Exemplar[] =
        await this.getExemplarsFromScenarioIdAndSkill(dbScenario.id, skill.id);

      await this.calcExemplarQualityAndTags(rawExemplars);

      while (!tags && rawExemplars.length) {
        currentExemplar = removeRandomArrayElement(rawExemplars, this.random);

        if (ignore.includes(currentExemplar.id)) continue;

        originalScenarioTags = await this.getTagsFromExemplarId(
          currentExemplar.id
        );

        // tags associated with exemplar must contain correct tag
        // that must also be the only one selected
        if (requiredCorrectTag) {
          const foundCorrectTag = originalScenarioTags.find((value) => value.id === requiredCorrectTag);
          if (foundCorrectTag) {
            originalScenarioTags = [foundCorrectTag]
          } else {
            // this exemplar didn't meet the correct tag restrictions, pick a different one
            continue;
          }
        }

        const possibleCorrectTags: DBTag[] = [...originalScenarioTags];

        selectedCorrectTags = [];

        const distractorCandidates = [...allPossibleRawTags];

        while (!tags && possibleCorrectTags.length) {
          selectedCorrectTags = [];
          while (selectedCorrectTags.length < currentTagsMax && possibleCorrectTags.length)
            selectedCorrectTags.push(removeRandomArrayElement(possibleCorrectTags, this.random));

          const distractorCount = 3 - selectedCorrectTags.length;
          tags = this.chooseTargetsAndDistractors(
            [],
            distractorCandidates,
            0,
            distractorCount,
            distractorCount,
            distractorCount,
            isTarget
          );
        }
      }
    }

    if (!tags || !currentExemplar) return;

    // From set of selected tags, make sure to inject the one not chosen as the exemplar
    // Iterate through list, if it doesn't contain the other one, replace one of the distractors
    if (requiredIncorrectTag) {
      const foundIncorrect = tags.find((value) => value.id === requiredIncorrectTag);
      if (!foundIncorrect) {
        const replacement = allPossibleRawTags.find((value) => value.id === requiredIncorrectTag);
        const indexToReplace = tags.findIndex((value) => isTarget(value) === -1);
        if (replacement && indexToReplace > -1) {
          tags[indexToReplace] = replacement;
        }
      }
    }
    tags = [...tags, ...selectedCorrectTags];
    ignoreList.push(currentExemplar.id);

    // For this question type we have a predetermined provider response and the user must select appropriate skill components/tags
    // For custom feedback, we find the skill component explanation for each option for that provider response
    const feedbackResults = await this.getResponseDefinitionFeedbackFromExemplars([currentExemplar], skill.id, dbScenario.id);

    const options: ResponseDefinition[] = tags.map(
      (tag): ResponseDefinition => {
        const correct = selectedCorrectTags.includes(tag);

        return {
          correct,
          id: tag.id,
          text: tag.reason,
          name: tag.name,
          reason: [
            {
              declarative: tag.explanation.markdown,
              imperative: tag.explanation.markdown,
              gerund: tag.explanation.markdown,
            },
          ],
          quality: tag.quality,
          // metadata: tag.metadata,
          tagData: [{ id: tag.id, name: tag.name }],
          customReviewFeedback: this.getFeedbackExplanationForExamplarAndTag(feedbackResults, currentExemplar, tag.id)

        };
      }
    );

    shuffleArray(options, this.random);

    const scenario: Scenario = {
      id: dbScenario.id,
      metadata: { ...dbScenario.metadata } as unknown as PatientData,
      content: dbScenario.content,
      name: dbScenario.name,
    };

    const rawInputPageData: RawInputPageData = {
      problemQuality: currentExemplar.quality > 0,
      problemType,
      question: currentExemplar.content.text,
      type,
      skill,
      options,
      hints,
      seed: this.random.seed,
      scenario,
      ignoreList,
    };
    return rawInputPageData;
  }

  /**
   * P4 style questions present a patient scenario and asks the user to pick a response that demonstrates a
   * particular tag/attribute, i.e. set of exemplars are shown as therapist responses that must be chosen to
   * match with a tag/attribute
   * 
   * @param request 
   * @returns 
   */
  private async generateP4(
    request: GetProblemRequest
  ): Promise<RawInputPageData | undefined> {
    this.resetRandomForRequest(request);

    const { type, ignore: ignoreList = [], requiredCorrectTag, requiredIncorrectTag } = request;
    const { secondary } = request.level;

    const skill: Skill = await this.getSkillFromName(request.skill);
    const scenarios: DBScenario[] = await this.getScenariosFromSkillId(
      skill.id
    );

    let dbScenario: DBScenario = scenarios[0];
    let exemplars: Exemplar[] | undefined = undefined;
    let exemplarCurrent: Exemplar | undefined = undefined;
    let tagChosenAsQuestionFocus: DBTag | undefined = undefined;

    const isTarget = (exemplar: Exemplar): number => {
      if (!tagChosenAsQuestionFocus) return 0;

      // if this is the first level, the qualities must not match
      if (secondary === 0 && exemplar.quality === exemplarCurrent?.quality) {
        return 0;
      }

      // if this is the second level, the qualities must match
      if (secondary === 1 && exemplar.quality !== exemplarCurrent?.quality) {
        return 0;
      }
      // the distractor cannot have the same tag as the current
      if (!exemplar.tags.every(tag=>tag.id !== tagChosenAsQuestionFocus!.id)){
          return 0;
      };

      // if this is the third level, only choose distractors with multiple tags
      if (secondary === 2 && exemplar.tags.length < 2) {
        return 0;
      }

      return -1;
    };

    while (!exemplars && scenarios.length) {
      dbScenario = removeRandomArrayElement(scenarios, this.random);

      const allExemplarOptionsForScenario: Exemplar[] =
        await this.getExemplarsFromScenarioIdAndSkill(dbScenario.id, skill.id);

      await this.calcExemplarQualityAndTags(allExemplarOptionsForScenario);

      const exemplarPool = [...allExemplarOptionsForScenario];
      while (
        !exemplars &&
        (exemplarCurrent = removeRandomArrayElement(exemplarPool, this.random))
      ) {
        if (ignoreList.includes(exemplarCurrent.id)) continue;

        let tagsForChosenExemplar: DBTag[];
        if (requiredCorrectTag) {
          // Selected tag that should be the focus of the question should exactly match the
          // required one passed in.
          // If the exemplar does NOT contain it we need to pick a different one.
          const matchingCorrectTag = exemplarCurrent.tags.find((value) => value.id === requiredCorrectTag);
          if (!matchingCorrectTag) {
            continue;
          } else {
            // Have only one tag to force it as the question focus
            tagsForChosenExemplar = [matchingCorrectTag]
          }
        } else {
          tagsForChosenExemplar = [...exemplarCurrent.tags];
        }

        while (!exemplars && tagsForChosenExemplar.length) {
          tagChosenAsQuestionFocus = removeRandomArrayElement(tagsForChosenExemplar, this.random);

          // If incorrect option is required, find the exemplar with matching conditions as the incorrect one,
          // make sure it is not picked, and manually add it
          const exemplarMatchingIncorrect = requiredIncorrectTag ? allExemplarOptionsForScenario.find((exemplar) => {
            return exemplar.tags.find((tag) => tag.id === requiredIncorrectTag) !== undefined;
          }) : undefined;
          const maxDistractors = exemplarMatchingIncorrect ? 1 : 2;

          // Pick out additional distractors (correct option is automatically added later)
          exemplars = this.chooseTargetsAndDistractors(
            (exemplarMatchingIncorrect) ? [exemplarMatchingIncorrect.id] : [],
            allExemplarOptionsForScenario,
            0,
            maxDistractors,
            2,
            2,
            isTarget
          );

          if (exemplarMatchingIncorrect) {
            exemplars?.push(exemplarMatchingIncorrect);
          }
        }
      }
    }

    if (!exemplars) return;

    exemplarCurrent && exemplars.push(exemplarCurrent);
    shuffleArray(exemplars, this.random);

    // For this question type, we have a predetermined skill component/tag we want to check against.
    // Custom feedback should attempt to fetch the response related to that component whether the 
    // provider response applies/does not apply
    const feedbackResults = await this.getResponseDefinitionFeedbackFromExemplars(exemplars, skill.id, dbScenario.id);

    const options: ResponseDefinition[] = exemplars.map(
      (exemplar): ResponseDefinition => {
        const correct = exemplar.id === exemplarCurrent?.id;
        const reason: TagText[] = exemplar.tags.map((tag): TagText => {
          return tag.reason;
        });
        const tagData: TagData[] = exemplar.tags.map((tag): TagData => {
          const { id, name } = tag;
          return { id, name };
        });

        let customFeedback = (tagChosenAsQuestionFocus) ? this.getFeedbackExplanationForExamplarAndTag(feedbackResults, exemplar, tagChosenAsQuestionFocus.id) : undefined;
        return {
          correct,
          id: exemplar.id,
          content: exemplar.content,
          quality: exemplar.quality,
          reason,
          tagData,
          customReviewFeedback: customFeedback
        };
      }
    );
    if (!exemplarCurrent || !tagChosenAsQuestionFocus) return;
    const rawInputPageData: RawInputPageData = {
      problemQuality: exemplarCurrent.quality > 0,
      problemType: 'singleChoice',
      type,
      skill,
      options,
      seed: this.random.seed,
      scenario: this.scenarioFromDBScenario(dbScenario),
      ignoreList: [exemplarCurrent.id],
      question: tagChosenAsQuestionFocus.reason.declarative.toLocaleLowerCase(),
    };
    return rawInputPageData;
  }

  /**
   * P6 style questions asks the user to select statements that demonstrate a skill either positively
   * or negatively
   * 
   * @param request 
   * @returns 
   */
  private async generateP6(
    request: GetProblemRequest
  ): Promise<RawInputPageData | undefined> {
    this.resetRandomForRequest(request);

    const { type, ignore: ignoreList = [] } = request;

    let problemQuality = this.getRandomProblemQuality();

    const skill: Skill = await this.getSkillFromName(request.skill);
    const scenarios: DBScenario[] = await this.getScenariosFromSkillId(
      skill.id
    );

    let dbScenario: DBScenario = scenarios[0];
    let exemplarStatementsSelected: Exemplar[] | undefined;

    const isTarget = (exemplar: Exemplar): number => {
      const exemplarQuality = exemplar.quality > 0;
      return !exemplarQuality === !problemQuality ? 1 : -1;
    };

    while (!exemplarStatementsSelected && scenarios.length) {
      dbScenario = removeRandomArrayElement(scenarios, this.random);

      if (ignoreList.includes(dbScenario.id)) continue;

      const exemplarsForScenario: Exemplar[] =
        await this.getExemplarsFromScenarioIdAndSkill(dbScenario.id, skill.id);

      await this.calcExemplarQualityAndTags(exemplarsForScenario);

      exemplarStatementsSelected = this.chooseTargetsAndDistractors(
        [],
        exemplarsForScenario,
        4,
        4,
        6,
        4,
        isTarget
      );
      // if we didn't find anything, flip the quality
      if (!exemplarStatementsSelected) {
        problemQuality = !problemQuality;
        exemplarStatementsSelected = this.chooseTargetsAndDistractors(
          [],
          exemplarsForScenario,
          4,
          4,
          6,
          4,
          isTarget
        );
      }
    }

    if (!exemplarStatementsSelected) return;

    const scenario = this.scenarioFromDBScenario(dbScenario);

    ignoreList.push(scenario.id);

    // Similar to P2:
    // This question type asks generally whether a selection of provider responses is good/poor
    // Custom feedback per response should simply pick the skill component (just first one if multiple) that
    // applies to a provider response and display that one
    const feedbackResults = await this.getResponseDefinitionFeedbackFromExemplars(exemplarStatementsSelected, skill.id, dbScenario.id);

    const options: ResponseDefinition[] = exemplarStatementsSelected.map(
      (exemplar): ResponseDefinition => {
        const exemplarQuality = exemplar.quality > 0;
        const correct = problemQuality === exemplarQuality;
        const reason: TagText[] = exemplar.tags.map((tag): TagText => {
          return tag.reason;
        });
        const tagData: TagData[] = exemplar.tags.map((tag): TagData => {
          const { id, name } = tag;
          return { id, name };
        });

        const customFeedback = (tagData.length > 0) ?
          this.getFeedbackExplanationForExamplarAndTag(feedbackResults, exemplar, tagData[0].id) : undefined;

        return {
          correct,
          id: exemplar.id,
          content: exemplar.content,
          quality: exemplar.quality,
          reason,
          tagData,
          customReviewFeedback: customFeedback
        };
      }
    );

    const hints = await this.getHintsForSkillAndQuality(
      skill.id,
      problemQuality
    );

    const problemType = 'multiChoice';

    const rawInputPageData: RawInputPageData = {
      problemQuality,
      problemType,
      type,
      skill,
      options,
      hints,
      seed: this.random.seed,
      scenario,
      ignoreList,
    };
    return rawInputPageData;
  }

  /**
   * Discourse style questions have a dialog between patient and therapist, the user must pick why each
   * therapist statement is a good/bad demonstration of the skill in general.
   * 
   * @param request 
   * @returns 
   */
  private async generateDiscourse(
    request: GetProblemRequest
  ): Promise<RawDiscoursePageData | undefined> {
    this.resetRandomForRequest(request);

    /*
    Look for scenario where one of the annotations has a correct tag and then also
    add the incorrect tag somewhere there too
    */
    const { exemplarId, ignore, type, requiredCorrectTag, requiredIncorrectTag } = request;
    let skillName = request.skill;
    let exemplar;

    if (exemplarId) {
      exemplar = await this.getAnnotationExemplarFromId(exemplarId);
      skillName = exemplar?.skills[0] || '';
    } else {
      const exemplars: Exemplar[] = await this.getAnnotationExemplarsForSkill(
        'annotation',
        skillName
      );

      let satisfiedRequirements = true;
      do {
        exemplar = removeRandomArrayElement(exemplars, this.random);
        if (!exemplar)
          break;
        const dbAnnotations: DBAnnotation[] = await this.getAnnotationForExemplar(
          exemplar.id
        );
        const annotations = this.discourseAnnotationsFromDBAnnotations(dbAnnotations);
        if (requiredCorrectTag) {
          satisfiedRequirements = annotations.find((value) => value.tag === requiredCorrectTag) !== undefined;
        }
      } while (!satisfiedRequirements || exemplar && ignore.includes(exemplar.id));
    }

    if (!exemplar) return;

    const dbAnnotations: DBAnnotation[] = await this.getAnnotationForExemplar(
      exemplar.id
    );
    const annotations = this.discourseAnnotationsFromDBAnnotations(dbAnnotations);

    const dbScenario: DBScenario = await this.getScenarioFromId(
      exemplar.scenario
    );
    const scenario: Scenario = this.scenarioFromDBScenario(dbScenario);
    const skill: Skill = await this.getSkillFromName(skillName);
    const allTagsForSkill: DBTag[] = await this.getTagsFromSkillId(skill.id);
    const tags: Tags = this.tagsFromRawTags(allTagsForSkill);
    const optionsPerAnnotation = generateAnnotationOptions(
      this.random,
      annotations, tags, requiredCorrectTag, requiredIncorrectTag);

    // Fetch json file
    // /content/discourse/review-feedback/{Module Id}/{exemplar-name}.json
    const customFeedback: DiscourseCustomFeedback[] = [];
    const feedbackData = await this.getDiscourseFeedback(skill.id, exemplar.id);
    if (feedbackData) {
      // In the raw page data
      // For each set of tag answer options for each statement, find the custom feedback
      // and make sure it is saved into the RawPageData
      customFeedback.push(...feedbackData.annotations);
    }
    

    const rawDiscoursePageData: RawDiscoursePageData = {
      id: exemplar.id,
      seed: this.random.seed,
      type,
      skill,
      ignoreList: [exemplar.id],
      scenario,
      content: exemplar.content?.slate?.root || [],
      annotationOptions: optionsPerAnnotation,
      annotationIdToCustomFeedback: customFeedback,
      annotations,
      tags,
    };

    return rawDiscoursePageData;
  }

  private async getDiscourseFeedback(moduleId: string, exemplarId: string): Promise<DisourceCustomFeedbackResponse | null> {
    const baseUrl = window.location.origin;
    const feedbackUrl = encodeURI(`${baseUrl}/content/discourse/review-feedback/${moduleId}/${exemplarId}.json`);
    try {
      const feedbackResponse = await fetch(feedbackUrl);
      if (feedbackResponse.ok) {
        const feedbackData: DisourceCustomFeedbackResponse = await feedbackResponse.json();
        return feedbackData;
      }
    } catch(error) {
      console.error("Feedback Fetch Failed: " + error);
    }
    return null;
  }

  /**
   * 
   * @param exemplars 
   * @param skillId 
   * @param scenarioId 
   * @returns Mapping from exemplar id to custom feedback if it exists for that exemplar
   */
  private async getResponseDefinitionFeedbackFromExemplars(exemplars: Exemplar[], skillId: string, scenarioId: string): Promise<Map<string, ResponseDefFeedbackResult>> {
    const customFeedbackRequests = exemplars.map((exemplar): ResponseDefFeedbackRequest => {
      return {
        exemplarId: exemplar.id,
        skillId,
        scenarioId
      };
    });
    const feedbackResults = await this.getResponseDefinitionFeedback(customFeedbackRequests);
    const exemplarIdToResult = new Map<string, ResponseDefFeedbackResult>();
    feedbackResults.forEach(result => {
      if (result.success) {
        exemplarIdToResult.set(result.req.exemplarId, result);
      }
    });
    return exemplarIdToResult;
  }

  /**
   * For multi-choice question types P2, P3, P4, and P6 custom feedback is centered on how well a tag
   * applies to a Provider Response.
   */
  private async getResponseDefinitionFeedback(requests: ResponseDefFeedbackRequest[]): Promise<ResponseDefFeedbackResult[]> {
    const fetchPromises = requests.map(async (requestDef): Promise<ResponseDefFeedbackResult> => {
      try {
        const baseUrl = window.location.origin;
        const url = encodeURI(`${baseUrl}/content/multichoice/review-feedback/${requestDef.skillId}/${requestDef.scenarioId}/${requestDef.exemplarId}.json`);
        const response = await fetch(url);

        // An HTTP error occured (like 404, file doesn't exist)
        if (!response.ok) {
          return {
            req: requestDef,
            contents: null,
            success: false
          };
        }

        const fileContent = await response.text();
        const parsedContent = JSON.parse(fileContent);
        return {
          req: requestDef,
          contents: parsedContent,
          success: true
        }
      } catch (error) {
        return {
          req: requestDef,
          contents: null,
          success: false
        };
      }
    });

    // Wait for all requests to finish
    const allResults = await Promise.all(fetchPromises);
    return allResults;
  }

  private getFeedbackExplanationForExamplarAndTag(feedbackResults: Map<string, ResponseDefFeedbackResult>, exemplar: Exemplar, tagId: string) {
    let customFeedback: string | undefined;
    if (feedbackResults.has(exemplar.id)) {
      const feedback = feedbackResults.get(exemplar.id)?.contents?.Results.find((value) => value.AttributeId == tagId);
      customFeedback = feedback?.Explanation;
    }

    return customFeedback;
  }

  private async generateDiscourseWithFixed(
    request: GetProblemRequest
  ): Promise<RawDiscourseFixedPageData | undefined> {
    this.resetRandomForRequest(request);

    const { exemplarId } = request;
    let skillName = request.skill;
    let exemplar: ExemplarWithFixed;

    if (exemplarId) {
      exemplar = await this.getAnnotationExemplarFromId(exemplarId) as ExemplarWithFixed;
      skillName = exemplar?.skills[0] || '';
      if (!exemplar.optionsId) return undefined;
    } else {
      const exemplars: ExemplarWithFixed[] = await this.getAnnotationExemplarsForSkill(
        'annotation-with-fixed',
        skillName
      ) as ExemplarWithFixed[];

      do {
        exemplar = removeRandomArrayElement(exemplars, this.random);
      } while (
        exemplar &&
        request.ignore.includes(exemplar.id)
      );
    }

    if (!exemplar) return;

    const skill: Skill = await this.getSkillFromName(skillName);
    const rawTags: DBTag[] = await this.getTagsFromSkillId(skill.id);
    const dbScenario: DBScenario = await this.getScenarioFromId(
      exemplar.scenario
    );
    const dbAnnotations: DBAnnotation[] = await this.getAnnotationForExemplar(
      exemplar.id
    );

    const scenario: Scenario = this.scenarioFromDBScenario(dbScenario);
    const annotations = this.discourseAnnotationsFromDBAnnotations(dbAnnotations);
    const tags: Tags = this.tagsFromRawTags(rawTags);
    const type = request.type;
    const dbOptions = await this.getFixedOptionsFromId(exemplar.optionsId);

    const options = this.fixedOptionsFromDbOptions(dbOptions);
    const question = this.parseFixedOptionQuestion(dbOptions, scenario.metadata.patient.name);
    const fixedOptionInfo: FixedOptionInfo = {
      tagId: exemplar.fixedTagId,
      fixedOptions: options,
      question,
      answer: exemplar.answer,
      reason: exemplar.reason,
    }
    const rawDiscourseFixedPageData: RawDiscourseFixedPageData= {
      id: exemplar.id,
      seed: this.random.seed,
      type,
      skill,
      ignoreList: [exemplar.id],
      scenario,
      content: exemplar.content?.slate?.root || [],
      annotations,
      tags,
      fixedOptionInfo
    };

    return rawDiscourseFixedPageData;
  }

  /**
   * Self eval type questions ask the user to enter a response to a patient scenario.
   * 
   * Then they self evaluate whether their response incorporated a positive attribute while avoiding
   * negative ones. They type a new response if they missed it.
   * 
   * @param request 
   * @returns 
   */
  private async generateSelfEval(
    request: GetProblemRequest
  ): Promise<RawEvalPageData | undefined> {
    this.resetRandomForRequest(request);

    const { exemplarId, ignore } = request;
    let skillName = request.skill;
    let exemplar: EvalExemplar | undefined;

    // Pick out a 'case' i.e. main statement the question will focus on.
    if (exemplarId) {
      exemplar = await this.getEvalExemplarFromId(exemplarId);
      skillName = exemplar?.skill;
    } else {
      const allSelfEvalExemplars: EvalExemplar[] = await this.getEvalExemplarsForSkill(
        skillName
      );
      const idsToIgnore = new Set<string>(ignore || []);
      const resultsToPickFrom = allSelfEvalExemplars.filter((value) => !idsToIgnore.has(value.id));
      if (resultsToPickFrom.length > 0) {
        exemplar = removeRandomArrayElement(resultsToPickFrom, this.random);
      }
    }
    if (!exemplar) return;
    const skill: Skill | undefined = await this.getSkillFromName(skillName);

    const allTagsForSkill: DBTag[] = await this.getTagsFromSkillId(skill.id);
    const dbScenario: DBScenario = await this.getScenarioFromId(
      exemplar.scenario
    );

    const tagsToAvoid: TagsAvoid = await this.avoidTagsFromRawTags(
      allTagsForSkill,
      exemplar.avoidTags
    );

    const scenario: Scenario = this.scenarioFromDBScenario(dbScenario);
    const dbTags = Array.isArray(exemplar.tag) ? exemplar.tag : [exemplar.tag];
    const tags = this.tagsFromRawTags(allTagsForSkill, dbTags);
    const type = request.type;

    // Why does this inherit from discourse page data???
    const rawEvalPageData: RawEvalPageData = {
      id: exemplar.id,
      seed: this.random.seed,
      type,
      skill,
      ignoreList: [exemplar.id],
      scenario,
      content: exemplar.content,
      annotations: [],
      tags,
      evalAnswers: {},
      tagsToAvoid,
      customQuestionPrompt: exemplar.prompt
    };

    return rawEvalPageData;
  }

  private async generatePeerEval(
    request: GetProblemRequest
  ): Promise<RawEvalPageData | undefined> {
    this.resetRandomForRequest(request);

    const { exemplarId, ignore } = request;
    let skillName = request.skill;
    let peerResult: PeerResult | undefined;

    if (exemplarId) {
      peerResult = await this.getPeerResultFromExemplarId(exemplarId);
      skillName = peerResult?.skill || '';
    } else {
      // TODO: Peer eval examples should have a group id,
      // don't pick examples from the same group if they are very similar???
      const allPeerResults: PeerResult[] = await this.getPeerResultsForSkill(
        skillName
      );
      const idsToIgnore = new Set<string>(ignore || []);
      const peerResultsToPickFrom = allPeerResults.filter((value) => !idsToIgnore.has(value.id));
      if (peerResultsToPickFrom.length > 0) {
        peerResult = removeRandomArrayElement(peerResultsToPickFrom, this.random);
      }
    }
    if (!peerResult) return;

    const skill: Skill = await this.getSkillFromName(skillName);
    const exemplar = await this.getEvalExemplarFromId(peerResult.exemplar);
    const rawTags: DBTag[] = await this.getTagsFromSkillId(skill.id);
    const dbScenario: DBScenario = await this.getScenarioFromId(
      exemplar.scenario
    );

    const tagsToAvoid: TagsAvoid = await this.avoidTagsFromRawTags(
      rawTags,
      exemplar.avoidTags
    );

    const scenario: Scenario = this.scenarioFromDBScenario(dbScenario);
    const dbTags = Array.isArray(exemplar.tag) ? exemplar.tag : [exemplar.tag];
    const tags = this.tagsFromRawTags(rawTags, dbTags);
    const type = request.type;

    const firstResponse = peerResult.firstResponse;

    const rawEvalPageData: RawEvalPageData = {
      id: peerResult.id,
      seed: this.random.seed,
      type,
      skill,
      ignoreList: [peerResult.id],
      scenario,
      content: exemplar.content,
      annotations: [],
      tags,
      evalAnswers: {},
      firstResponse,
      tagsToAvoid,
    };

    return rawEvalPageData;
  }

  private fixedOptionsFromDbOptions(dbOptions: FixedOption): RawFixedOptions {
    const options: RawFixedOptions = {};
    dbOptions.options.forEach((option)=>{
      options[option] = {name: option, selected: false};
    });
    return options;
  }

  private parseFixedOptionQuestion(dbOptions: FixedOption, name: string): string {
    let question = dbOptions.question;
    const parseOptions = dbOptions.parseOptions;
    parseOptions.forEach((v) => {
      switch (v) {
        case 'name' : {
          question = question.replace(`{name}`,name);
        }
      }
    });
    return question;
}

  private async generateFixedOption(
    request: GetProblemRequest
  ): Promise<RawFixedOptionPageData | undefined> {
    this.resetRandomForRequest(request);

    const exemplarId = request.exemplarId;
    const ignore: string[] = request.ignore || [];
    let exemplars;
    let skillId = request.skill
    if (exemplarId) {
      const exemplar = await this.getFixedOptionExemplarFromId(exemplarId);
      skillId = exemplar?.skillId;
      exemplars = [exemplar];
    } else {
      exemplars = await this.getFixedOptionExemplarsForSkill(skillId);
    }
    
    let exemplar: FixedOptionExemplar;
    const skill: Skill = await this.getSkillFromName(skillId);

    while (exemplars?.length) {
      exemplar = removeRandomArrayElement(exemplars, this.random);
      const {id: exemplarId, tagId, answer, reason } = exemplar;

      if (ignore.includes(exemplarId)) continue;

      const dbOptions = await this.getFixedOptionsFromId(exemplar.optionsId);
      const dbScenario = await this.getScenarioFromId(exemplar.scenarioId);
      const scenario: Scenario = this.scenarioFromDBScenario(dbScenario);

      if (dbOptions && scenario) {
        const options = this.fixedOptionsFromDbOptions(dbOptions);
        const question = this.parseFixedOptionQuestion(dbOptions, scenario.metadata.patient.name);
        const rawFixedOptionPageData: RawFixedOptionPageData = {
          scenario,
          type: SFFProblemType.FixedOption,
          options,
          exemplarId,
          tagId,
          question,
          answer,
          reason,
          skill,
          ignoreList: [exemplarId],
          seed: this.random.seed
        }
        return rawFixedOptionPageData;
      }
    }
    return undefined
  }

  /**
   * Generate a question that matches the given request parameters
   * 
   * @param request 
   * @returns undefined if a problem could not be created within the parameters
   */
  public static async getProblem(
    request: GetProblemRequest
  ): Promise<RawPageData | undefined> {
    const { type } = request;
    const problemGenerator = ProblemGenerator.getInstance();

    const generateFn = problemGenerator.typeGenerators[type];
    return generateFn && generateFn(request);
  }

  public static async getFixedDescriptionData() {
    const problemGenerator = ProblemGenerator.getInstance();
    const allSkills = await skillDB.getAllDocs();
    const skillIdToTagMap: Record<string, Record<string, Tag>> = {};
    for (const skillId in allSkills) {
      const tagIdToTagMap: Record<string, Tag> = {};
      const tags: Tag[] = await problemGenerator.getTagsFromSkillId(skillId);
      tags.forEach(tag => {
        tagIdToTagMap[tag.id] = tag;
      });
      skillIdToTagMap[skillId] = tagIdToTagMap;
    }

    return {
      skillDescriptions: allSkills,
      tagDescriptions: skillIdToTagMap
    };
  }
}
